import { resolvePicSrc } from "./cms/branch-images.js";
import {
  getGalleryList,
  addGalleryItem,
  removeGalleryItem,
  getHeroSlides,
  addHeroSlide,
  removeHeroSlide,
  getStoryParagraphs,
  addStoryParagraph,
  removeStoryParagraph,
  isAdminAuthed,
} from "./cms/store.js";
import { refreshInlineCms, showCmsToast } from "./cms/inline.js";

export function renderHomeCmsContent() {
  if (document.body.dataset.page !== "home") return;

  renderHeroSlides();
  renderStoryParagraphs();
  renderGallery();
  renderHomeCmsActions();
}

export function bindHomeCmsUi() {
  if (document.body.dataset.page !== "home") return;

  initHomeCmsButtons();
  refreshInlineCms();
}

async function rerenderHome() {
  renderHomeCmsContent();
  bindHomeCmsUi();
  const { initReveal } = await import("./site.js");
  const { syncHomeSeoMeta } = await import("./seo.js");
  syncHomeSeoMeta();
  initReveal();
}

function renderHeroSlides() {
  const inner = document.getElementById("hero-carousel-inner");
  if (!inner) return;

  const slides = getHeroSlides();
  inner.innerHTML = slides
    .map(
      (slide, index) => `
    <div class="carousel-item${index === 0 ? " active" : ""}" data-cms-hero-item data-cms-hero-index="${index}">
      ${renderRemoveButton("hero", index, slide.alt)}
      <img src="${escapeAttr(resolvePicSrc(slide.src))}" alt="${escapeAttr(slide.alt)}" data-cms-hero-field="src">
    </div>
  `
    )
    .join("");

}

function renderStoryParagraphs() {
  const root = document.getElementById("story-paragraphs-root");
  if (!root) return;

  const paragraphs = getStoryParagraphs();
  root.innerHTML = paragraphs
    .map(
      (text, index) => `
    <div class="bm-story-paragraph" data-cms-story-item data-cms-story-index="${index}">
      ${renderRemoveButton("story", index, "הפסקה")}
      <p data-cms-story-field="text">${escapeHtml(text)}</p>
    </div>
  `
    )
    .join("");
}

function renderGallery() {
  const root = document.getElementById("gallery-root");
  const actions = document.getElementById("gallery-cms-actions");
  if (!root) return;

  const items = getGalleryList();
  root.innerHTML = items
    .map(
      (item, index) => `
    <article class="product reveal bm-gallery-item" data-cms-gallery-item data-cms-gallery-index="${index}">
      ${renderRemoveButton("gallery", index, item.alt)}
      <div class="img-container">
        <img src="${escapeAttr(resolvePicSrc(item.src))}" alt="${escapeAttr(item.alt)}" class="product-img" data-cms-gallery-field="src">
      </div>
    </article>
  `
    )
    .join("");

  if (actions) {
    actions.innerHTML = isAdminAuthed()
      ? `<button type="button" class="bm-cms-list-add" data-cms-gallery-add>+ הוסף תמונה לגלריה</button>`
      : "";
  }
}

function renderRemoveButton(type, index, label) {
  if (!isAdminAuthed()) return "";
  return `<button type="button" class="bm-cms-item-remove" data-cms-remove="${type}" data-cms-remove-index="${index}" aria-label="הסר את ${label}">×</button>`;
}

function renderHomeCmsActions() {
  const heroActions = document.getElementById("hero-cms-actions");
  const storyActions = document.getElementById("story-cms-actions");

  if (heroActions) {
    heroActions.innerHTML = isAdminAuthed()
      ? `<button type="button" class="bm-cms-list-add" data-cms-hero-add>+ הוסף שקופית לקרוסלה</button>`
      : "";
  }

  if (storyActions) {
    storyActions.innerHTML = isAdminAuthed()
      ? `<button type="button" class="bm-cms-list-add" data-cms-story-add>+ הוסף פסקה</button>`
      : "";
  }
}

function initHomeCmsButtons() {
  if (!isAdminAuthed()) return;

  document.querySelectorAll("[data-cms-remove]").forEach((btn) => {
    if (btn.dataset.cmsRemoveReady === "1") return;
    btn.dataset.cmsRemoveReady = "1";

    btn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      const type = btn.dataset.cmsRemove;
      const index = Number(btn.dataset.cmsRemoveIndex);
      if (!type || Number.isNaN(index)) return;

      const label =
        type === "story"
          ? "הפסקה"
          : btn.closest("[data-cms-gallery-item], [data-cms-hero-item]")?.querySelector("img")?.alt ||
            "הפריט";

      if (!confirm(`להסיר את ${label}?`)) return;

      let ok = false;
      if (type === "gallery") ok = removeGalleryItem(index);
      else if (type === "hero") ok = removeHeroSlide(index);
      else if (type === "story") ok = removeStoryParagraph(index);

      if (!ok) {
        showCmsToast("חייב להישאר לפחות פריט אחד", "error");
        return;
      }

      rerenderHome();
      showCmsToast("הפריט הוסר");
    });
  });

  document.querySelectorAll("[data-cms-gallery-add]").forEach((btn) => {
    if (btn.dataset.cmsAddReady === "1") return;
    btn.dataset.cmsAddReady = "1";
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      addGalleryItem();
      rerenderHome();
      showCmsToast("נוספה תמונה לגלריה");
    });
  });

  document.querySelectorAll("[data-cms-hero-add]").forEach((btn) => {
    if (btn.dataset.cmsAddReady === "1") return;
    btn.dataset.cmsAddReady = "1";
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      addHeroSlide();
      rerenderHome();
      showCmsToast("נוספה שקופית לקרוסלה");
    });
  });

  document.querySelectorAll("[data-cms-story-add]").forEach((btn) => {
    if (btn.dataset.cmsAddReady === "1") return;
    btn.dataset.cmsAddReady = "1";
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      addStoryParagraph();
      rerenderHome();
      showCmsToast("נוספה פסקה");
    });
  });
}

function escapeAttr(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
